import { useState, useMemo } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputAdornment,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { map, uniqBy, xor } from "lodash-es";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import {
  ResidentialZoneRestrictions,
  ResidentialZoneWeightFormItem,
  ResidentialZoneWeightItem,
  Scenario,
} from "src/types";
import { TableHeadCell } from "./TableHeadCell";
import { useResidentialZone } from "src/hooks/residential-zones";
import { RestrictionRow } from "./ResctrictionRow";
import { Field, Form, Formik } from "formik";
import { Spacer } from "src/components/Spacer";
import { TextField } from "formik-mui";
import { isDisabled } from "src/utils/permission.utils";

export type OrderColumn = "substance" | "dwelling" | "fuel" | "appliance";
export type FilterColumn = "substance" | "dwelling" | "fuel" | "appliance";
export type OrderDirection = "asc" | "desc";
export type Filters = Record<FilterColumn, Array<string | number>>;

const INITIAL_FILTERS = {
  substance: [],
  dwelling: [],
  fuel: [],
  appliance: [],
};

interface Props {
  residentialZoneId: number | string;
  scenario: Scenario;
  readOnly: boolean;
  uploadNumber: number;
  uploadWeightList?: ResidentialZoneWeightItem[];
  onChange: (restrictions: ResidentialZoneRestrictions) => void;
}

export function ResidentialZoneRestrictionsTable(props: Props) {
  const { scenario, uploadWeightList, uploadNumber, readOnly, onChange, residentialZoneId } = props;
  const { t } = useTranslation();
  const { data: residentialZone } = useResidentialZone(scenario.id, residentialZoneId);
  // dirty data is non-persisted fleet config
  const [orderBy, setOrderBy] = useState<OrderColumn>("substance");
  const [orderDirection, setOrderDirection] = useState<OrderDirection>("asc");
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const initialValues = useMemo(() => {
    if (!residentialZone) return undefined;
    return {
      ...residentialZone,
      weightList: (uploadWeightList || residentialZone.weightList).map((m, i) => ({ ...m, originalIndex: i })),
    };
  }, [residentialZone, uploadWeightList]);

  /**
   * Memo
   */

  const visualData = useMemo(() => {
    if (!initialValues) return undefined;

    const filteredWeightList = filterData(initialValues.weightList, filters);
    const sortedWeightList = sortData(filteredWeightList, orderBy, orderDirection);
    return { ...initialValues, weightList: sortedWeightList };
  }, [filters, orderBy, orderDirection, initialValues]);

  function handleSort(column: OrderColumn) {
    if (column !== orderBy) {
      setOrderBy(column);
      setOrderDirection("asc");
    }
    if (column === orderBy) {
      toggleOrderDirection();
    }
  }

  function toggleOrderDirection() {
    if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else if (orderDirection === "desc") {
      setOrderDirection("asc");
    }
  }

  function updateFilters(filterKey: FilterColumn, updatedFilters: Array<string | number>) {
    setFilters({
      ...filters,
      [filterKey]: xor(filters[filterKey], updatedFilters),
    });
  }

  const validationSchema = Yup.object().shape({
    totalWeight: Yup.number().min(0, "Weight must be 0 or greater"),
    weightList: Yup.array().of(
      Yup.object().shape({
        weight: Yup.number().min(0, "Weight must be 0 or greater"),
      })
    ),
  });

  if (!initialValues) return null;

  return (
    <Formik initialValues={initialValues} onSubmit={onChange} key={uploadNumber} validationSchema={validationSchema}>
      {() => (
        <StyledForm id="weight-form">
          <div className="energy-efficiency-measure">
            <Typography gutterBottom>{t("label.energyEfficiencyMeasure")}</Typography>
            <Spacer />
            <Field
              name="totalWeight"
              component={TextField}
              disabled={scenario ? isDisabled(scenario.permissions.updateResidential) : false}
              size="small"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                min: 0,
              }}
            />
          </div>
          <Typography variant="h3" gutterBottom>
            {t("label.specificMeasures")}
            {/* {t("label.residentialZones")} */}
          </Typography>
          <TableContainer component={Paper}>
            <Table stickyHeader className="table">
              <TableHead>
                <TableRow>
                  <TableHeadCell<OrderColumn>
                    dataKey="substance"
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    tooltip={t("label.filterByPollutant")}
                    onSort={() => handleSort("substance")}
                    onFilter={(filters) => updateFilters("substance", filters)}
                    filters={uniqBy(
                      map(residentialZone?.weightList, (item) => ({
                        id: item["residentialEmission"]["substance"]["name"],
                        label: item["residentialEmission"]["substance"]["name"],
                      })),
                      "id"
                    )}
                    activeFilterIds={filters.substance}
                  >
                    {t("label.pollutant")}
                  </TableHeadCell>

                  <TableHeadCell<OrderColumn>
                    dataKey="dwelling"
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    tooltip={t("label.filterByDwelling")}
                    onSort={() => handleSort("dwelling")}
                    onFilter={(filters) => updateFilters("dwelling", filters)}
                    filters={uniqBy(
                      map(residentialZone?.weightList, (item) => ({
                        id: item["residentialEmission"]["dwelling"]["name"],
                        label: item["residentialEmission"]["dwelling"]["translation"],
                      })),
                      "id"
                    )}
                    activeFilterIds={filters.dwelling}
                  >
                    {t("label.dwelling")}
                  </TableHeadCell>

                  <TableHeadCell<OrderColumn>
                    dataKey="fuel"
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    tooltip={t("label.filterByFuel")}
                    onSort={() => handleSort("fuel")}
                    onFilter={(filters) => updateFilters("fuel", filters)}
                    filters={uniqBy(
                      map(residentialZone?.weightList, (item) => ({
                        id: item["residentialEmission"]["fuel"]["name"],
                        label: item["residentialEmission"]["fuel"]["translation"],
                      })),
                      "id"
                    )}
                    activeFilterIds={filters.fuel}
                  >
                    {t("label.fuel")}
                  </TableHeadCell>

                  <TableHeadCell<OrderColumn>
                    dataKey="appliance"
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    tooltip={t("label.filterByAppliance")}
                    onSort={() => handleSort("appliance")}
                    onFilter={(filters) => updateFilters("appliance", filters)}
                    filters={uniqBy(
                      map(residentialZone?.weightList, (item) => ({
                        id: item["residentialEmission"]["appliance"]["name"],
                        label: item["residentialEmission"]["appliance"]["translation"],
                      })),
                      "id"
                    )}
                    activeFilterIds={filters.appliance}
                  >
                    {t("label.appliance")}
                  </TableHeadCell>

                  <TableCell>{t("label.percentage")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visualData?.weightList.map((row: ResidentialZoneWeightFormItem) => {
                  return (
                    <RestrictionRow
                      index={row.originalIndex}
                      key={[
                        row.residentialEmission.substance.id,
                        row.residentialEmission.dwelling.name,
                        row.residentialEmission.fuel.name,
                        row.residentialEmission.appliance.name,
                      ].join()}
                      substance={row.residentialEmission.substance.name}
                      dwelling={row.residentialEmission.dwelling.translation}
                      fuel={row.residentialEmission.fuel.translation}
                      appliance={row.residentialEmission.appliance.translation}
                      weight={row.weight}
                      readOnly={readOnly}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledForm>
      )}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .MuiTableContainer-root {
    height: 100%;
    overflow-y: auto;
  }

  .MuiTableBody-root {
    .MuiTableRow-root {
      &:nth-of-type(odd) {
        background-color: #f7f7f7;
      }
    }
  }

  .MuiFormHelperText-root {
    display: none;
  }

  .MuiTableCell-stickyHeader {
    background-color: #fafafa;
  }

  .MuiTableRow-root.total-weight {
    text-transform: uppercase;
  }
`;

function filterData(data: ResidentialZoneWeightFormItem[], filters: Filters): ResidentialZoneWeightFormItem[] {
  const filterKeys = Object.keys(filters) as FilterColumn[];

  return data.filter((item) => {
    const matches = filterKeys.reduce((matchAcc: boolean[], filterKey: FilterColumn) => {
      const emptyFilterColumn = filters[filterKey].length === 0;

      if (emptyFilterColumn) return [...matchAcc, true];
      else {
        const filterMatch = filters[filterKey].includes(item.residentialEmission[filterKey].name);
        return [...matchAcc, filterMatch];
      }
    }, []);

    return !matches.includes(false);
  });
}

function sortData(data: ResidentialZoneWeightFormItem[], column: OrderColumn, direction: OrderDirection) {
  return defaultSort(data, column, direction);
}

export function defaultSort(data: ResidentialZoneWeightFormItem[], column: OrderColumn, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = a.residentialEmission[column].name.toString().toUpperCase(); // ignore upper and lowercase
    const nameB = b.residentialEmission[column].name.toString().toUpperCase(); // ignore upper and lowercase

    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}
